import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

export interface OccaIntegrationConfig {
  id: string;
  isEnabled: boolean;
  clientId: string;
  hasSecret: boolean;
  baseUrl: string;
  lastIndexSyncAt: string | null;
  indexItemCount: number;
  defaultSupplierId: string | null;
  updatedAt: string;
}

export interface OccaIndexJob {
  id: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
  progress: {
    label: string;
    completed: number;
    total: number;
    percent: number;
  };
  result: { indexed: number; skipped: number; pages: number } | null;
  error: string | null;
}

interface OccaIntegrationProps {
  config: OccaIntegrationConfig | null;
  indexJob: OccaIndexJob | null;
  onEnable: (input: {
    clientId: string;
    secret: string;
    baseUrl: string;
  }) => Promise<void>;
  onDisable: () => Promise<void>;
  onSyncIndex: () => Promise<OccaIndexJob>;
  onRefreshJob: () => Promise<void>;
}

export function OccaIntegration({
  config,
  indexJob,
  onEnable,
  onDisable,
  onSyncIndex,
  onRefreshJob,
}: OccaIntegrationProps) {
  const [clientId, setClientId] = useState('');
  const [secret, setSecret] = useState('');
  const [baseUrl, setBaseUrl] = useState('https://dev-api.occastore.com');
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [setupOpen, setSetupOpen] = useState(false);
  const [confirmDisable, setConfirmDisable] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleEnable = async () => {
    if (!clientId.trim() || !secret.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await onEnable({
        clientId: clientId.trim(),
        secret: secret.trim(),
        baseUrl: baseUrl.trim() || 'https://dev-api.occastore.com',
      });
      setSetupOpen(false);
      setSecret('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save Occa integration');
    } finally {
      setSaving(false);
    }
  };

  const handleSyncIndex = async () => {
    setSyncing(true);
    setError(null);
    try {
      await onSyncIndex();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start index sync');
    } finally {
      setSyncing(false);
    }
  };

  const activeJob =
    indexJob && (indexJob.status === 'queued' || indexJob.status === 'running') ? indexJob : null;

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="text-lg">Occa Store</CardTitle>
            <CardDescription>
              Enrich Express products from Occa catalogue data matched by unit barcode.
            </CardDescription>
          </div>
          {config?.isEnabled ? (
            <Badge variant="outline" className="border-green-200 bg-green-50 text-green-800">
              Connected
            </Badge>
          ) : (
            <Badge variant="outline">Not configured</Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && <p className="text-sm text-destructive">{error}</p>}

        {!config?.isEnabled ? (
          <Dialog open={setupOpen} onOpenChange={setSetupOpen}>
            <Button onClick={() => setSetupOpen(true)}>Connect Occa</Button>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Connect Occa Bridge API</DialogTitle>
                <DialogDescription>
                  Use the Client ID and HMAC secret issued by Occa. Credentials are stored per
                  organization.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="occa-base-url">Bridge base URL</Label>
                  <Input
                    id="occa-base-url"
                    value={baseUrl}
                    onChange={(e) => setBaseUrl(e.target.value)}
                    placeholder="https://dev-api.occastore.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="occa-client-id">Client ID</Label>
                  <Input
                    id="occa-client-id"
                    value={clientId}
                    onChange={(e) => setClientId(e.target.value)}
                    autoComplete="off"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="occa-secret">HMAC secret</Label>
                  <Input
                    id="occa-secret"
                    type="password"
                    value={secret}
                    onChange={(e) => setSecret(e.target.value)}
                    autoComplete="new-password"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setSetupOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleEnable} disabled={saving || !clientId.trim() || !secret.trim()}>
                  {saving ? 'Saving…' : 'Save connection'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        ) : (
          <>
            <dl className="grid gap-3 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-muted-foreground">Client ID</dt>
                <dd className="font-medium">{config.clientId}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Base URL</dt>
                <dd className="font-medium break-all">{config.baseUrl}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Index entries</dt>
                <dd className="font-medium">{config.indexItemCount.toLocaleString()}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Last index sync</dt>
                <dd className="font-medium">
                  {config.lastIndexSyncAt
                    ? new Date(config.lastIndexSyncAt).toLocaleString()
                    : 'Never'}
                </dd>
              </div>
            </dl>

            {activeJob && (
              <div className="rounded-lg border bg-muted/30 p-3 text-sm">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <span>{activeJob.progress.label}</span>
                  <span className="tabular-nums text-muted-foreground">
                    {activeJob.progress.percent}%
                  </span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full bg-primary transition-all"
                    style={{ width: `${activeJob.progress.percent}%` }}
                  />
                </div>
              </div>
            )}

            {indexJob?.status === 'completed' && indexJob.result && (
              <p className="text-sm text-muted-foreground">
                Last sync indexed {indexJob.result.indexed.toLocaleString()} barcodes across{' '}
                {indexJob.result.pages} stock pages.
              </p>
            )}

            {indexJob?.status === 'failed' && indexJob.error && (
              <p className="text-sm text-destructive">{indexJob.error}</p>
            )}

            <div className="flex flex-wrap gap-2">
              <Button onClick={handleSyncIndex} disabled={syncing || Boolean(activeJob)}>
                {syncing || activeJob ? 'Syncing index…' : 'Sync catalogue index'}
              </Button>
              {activeJob && (
                <Button variant="outline" onClick={() => void onRefreshJob()}>
                  Refresh progress
                </Button>
              )}
              <Button variant="outline" onClick={() => setConfirmDisable(true)}>
                Disable
              </Button>
            </div>

            <p className="text-xs text-muted-foreground">
              Run an index sync first, then use &quot;Enrich from Occa&quot; on products with a unit
              barcode. Matching uses the cached barcode index built from Occa stock SKUs.
            </p>
          </>
        )}

        <Dialog open={confirmDisable} onOpenChange={setConfirmDisable}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Disable Occa integration?</DialogTitle>
              <DialogDescription>
                Product enrichment from Occa will stop until you reconnect.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmDisable(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={async () => {
                  await onDisable();
                  setConfirmDisable(false);
                }}
              >
                Disable
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
